// src/utils/apiFetchShim.ts
// Ensure cookies are sent, handle API base URL, remove empty Authorization headers, handle 401 -> redirect

// Determine API base URL based on environment
const getApiBaseUrl = (): string => {
  // Check for environment variables (supports both Next.js and Create React App)
  if (typeof process !== 'undefined' && process.env) {
    return (
      process.env.NEXT_PUBLIC_API_URL ||
      process.env.REACT_APP_API_URL ||
      ''
    );
  }
  return '';
};

const API_BASE_URL = getApiBaseUrl();

// Log API URL on startup (helps debugging)
if (typeof window !== 'undefined') {
  console.log('🌐 API Base URL:', API_BASE_URL);
}

if (typeof window !== 'undefined' && window.fetch) {
  const originalFetch = window.fetch.bind(window);
  
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    // Convert input to string URL
    let url: string;
    if (typeof input === 'string') {
      url = input;
    } else if (input instanceof URL) {
      url = input.href;
    } else {
      url = input.url;
    }

    // If URL is relative (starts with /), prepend API base URL
    if (url.startsWith('/')) {
      url = `${API_BASE_URL}${url}`;
    }

    const opts: RequestInit = { ...(init || {}) };
    
    // Ensure cookies are sent
    opts.credentials = opts.credentials || 'include';

    // Normalize and sanitize headers
    const headers = new Headers(opts.headers || {});
    const auth = headers.get('Authorization') || '';
    if (/^Bearer\s*(?:null|undefined|\s*)$/i.test(auth)) {
      headers.delete('Authorization');
    }
    
    // Ensure Content-Type is present for JSON bodies when not provided
    if (!headers.has('Content-Type') && opts.body && typeof opts.body === 'string') {
      headers.set('Content-Type', 'application/json');
    }
    opts.headers = headers;

    // Make the request with the updated URL
    const res = await originalFetch(url, opts);

    // Handle 401 responses
    if (res.status === 401) {
      try {
        // Clear any legacy client-side tokens (best-effort)
        if (typeof localStorage !== 'undefined') {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
        }
      } catch (e) { /* ignore */ }

      // DON'T auto-redirect to login - let AuthContext handle it gracefully
      // Only redirect if we're NOT already on login/register pages
      if (typeof window !== 'undefined') {
        const currentPath = window.location.pathname;
        const PUBLIC_PATHS = ['/', '/login', '/register', '/forgot-password', '/reset-password', '/verify-email', '/terms', '/privacy', '/data-deletion', '/waitlist'];
        const isAuthPage = PUBLIC_PATHS.some(p => currentPath === p || currentPath.startsWith(p + '/'));

        // Only redirect to login if we're on a protected page
        if (!isAuthPage) {
          console.log('🔐 401 response on protected page - redirecting to login');
          window.location.assign('/login');
        } else {
          console.log('🔐 401 response on auth page - letting AuthContext handle it');
        }
      }
    }

    return res;
  };
}

export {};